export const PERMS = {
  admin: {
    access: "admin.access",
    detailsAccess: "admin.details.access",
    create: "admin.create",
    toggle: "admin.status.toggle",
    delete: "admin.delete",
    usersManage: "admin.users.manage",
    settingsManage: "admin.settings.manage",
    changeEmail: "admin.change.email",
    changeOrg: "admin.change.org",
    updatePhone: "admin.update.phone",
  },
  // Exams & Grading. Mirrors the backend's ExamPermissionSeeder exactly — these
  // strings are the same eighteen the API middleware checks.
  //
  // The older keys below (create/edit/delete/dietCreate/…) named permissions
  // that were never seeded — "exam.create", "exam.diet.status.toggle" and the
  // rest exist in no permissions table, so every AccessGate using them was
  // hidden from everyone except super_admin regardless of what an account had
  // been granted. They are kept as aliases, pointed at the capability that
  // actually governs the action, so the gates already written across the exam
  // pages start working instead of silently denying.
  //
  // Split by capability, not by page: publishing (closing grading, Chief
  // Examiner) and releasing (showing candidates their result, secretariat) are
  // two different offices and must never be conferred together.
  exam: {
    access: "exam.access",
    manage: "exam.manage",

    dietAccess: "exam.diet.access",
    dietManage: "exam.diet.manage",

    centerAccess: "exam.center.access",
    centerManage: "exam.center.manage",

    slotAccess: "exam.slot.access",
    slotManage: "exam.slot.manage",

    sessionManage: "exam.session.manage",
    applicationManage: "exam.application.manage",

    gradeAccess: "exam.grade.access",
    gradeManage: "exam.grade.manage",
    marking: "exam.marking",

    examinerAssign: "exam.examiner.assign",
    resultPublish: "exam.result.publish",
    resultRelease: "exam.result.release",

    moderationAccess: "exam.moderation.access",
    moderationManage: "exam.moderation.manage",

    // ── Aliases for gates written before the permissions existed ──────────
    detailsAccess: "exam.access",
    detailsDietAccess: "exam.diet.access",
    detailsCenterAccess: "exam.center.access",

    create: "exam.manage",
    edit: "exam.manage",
    toggle: "exam.manage",
    delete: "exam.manage",

    dietCreate: "exam.diet.manage",
    dietEdit: "exam.diet.manage",
    dietToggle: "exam.diet.manage",
    dietDelete: "exam.diet.manage",

    centerCreate: "exam.center.manage",
    centerEdit: "exam.center.manage",
    centerToggle: "exam.center.manage",
    centerDelete: "exam.center.manage",

    slotToggle: "exam.slot.manage",
    gradeToggle: "exam.grade.manage",
  },

  billing: {
    access: "billing.access",
    typeAccess: "billing.type.access",
    typeCreate: "billing.type.create",
    typeUpdate: "billing.type.update",
    typeDelete: "billing.type.delete",

    priceAccess: "billing.price.access",
    priceUpdate: "billing.price.update",

    // The dues bands and the demand note sit on their own privileges rather
    // than riding on the section-wide billing.access.
    bandAccess: "billing.band.access",
    demandNoteAccess: "billing.demand_note.access",
    toggle: "department.toggle",
    delete: "department.delete",
    detailsAccess: "department.details.access",

    advancePost: "billing.advance.post",

    // Receipt amendments — editing or deleting an already-receipted payment.
    // Request and approve are separate capabilities: the admin who raises an
    // amendment must not be the one who approves it. Mirrors the backend's
    // ReceiptAmendmentPermissionSeeder.
    amendmentAccess: "billing.amendment.access",
    amendmentRequest: "billing.amendment.request",
    amendmentApprove: "billing.amendment.approve",

    // Correcting the gateway reference on an UNSETTLED payment. Its own
    // privilege rather than part of the amendment family: nothing has been
    // receipted or posted yet, so there is no second admin to wait for — but
    // the reference is the only key verification has, so being able to read
    // the register is not enough to repoint a row at a different transaction.
    // Mirrors the backend's PaymentReferenceEditPermissionSeeder.
    referenceUpdate: "billing.reference.update",
  },

  // Admin events section. Split by capability rather than by page — desk staff
  // work the scanner and nothing else, and awarding CPD is kept apart from
  // reading it. Mirrors the backend's EventPermissionSeeder.
  event: {
    access: "event.access",
    create: "event.create",
    update: "event.update",
    delete: "event.delete",
    lifecycle: "event.lifecycle",
    registrationManage: "event.registration.manage",
    inventoryManage: "event.inventory.manage",
    scannerUse: "event.scanner.use",
    cpdAccess: "event.cpd.access",
    cpdManage: "event.cpd.manage",
    instituteManage: "event.institute.manage",
  },

  // Secretariat stores. ACCESS reads the register; MANAGE maintains the item
  // list and the stores themselves; MOVEMENT records receipts, issues,
  // transfers, sales and returns. Mirrors the backend's StorePermissionSeeder.
  store: {
    access: "store.access",
    manage: "store.manage",
    movement: "store.movement",
  },

  // The national positions register — past presidents and secretaries, the
  // Board of Trustees, the national bodies, external representatives. MANAGE
  // sits on top of ACCESS because appointing somebody grants them a role.
  // Mirrors the backend's NationalBodyPermissionSeeder.
  nationalBody: {
    access: "national_body.access",
    manage: "national_body.manage",
  },
  committee: {
    access: "committee.access",
    create: "committee.create",

    delete: "committee.delete",

    update: "committee.update",
    manage_members: "committee.manage.members",
    managePermission: "committee.manage.permission",
  },

  department: {
    access: "department.access",
    create: "department.create",
    update: "department.update",
    toggle: "department.toggle",
    delete: "department.delete",
    detailsAccess: "department.details.access",
  },
  designation: {
    access: "designation.access",
    create: "designation.create",
    update: "designation.update",
    toggle: "designation.toggle",
    delete: "designation.delete",
    detailsAccess: "designation.details.access",
  },
  access: {
    sync: "designation.sync.permission",
    revoke: "designation.revoke.permission",
    update: "designation.update",
    toggle: "designation.toggle",
    delete: "designation.delete",
    detailsAccess: "designation.details.access",
  },
  executive_office: {
    sync: "executive_office.sync.permission",
    revoke: "executive_office.revoke.permission",
    update: "executive_office.update",
    toggle: "executive_office.toggle",
    delete: "executive_office.delete",
    access: "executive_office.access",
    create: "executive_office.create",
    detailsAccess: "executive_office.details.access",
    assign: "executive_office.assign",
  },
  executive_set: {
    sync: "executive_set.sync.permission",
    revoke: "executive_set.revoke.permission",
    update: "executive_set.update",
    toggle: "executive_set.toggle",
    delete: "executive_set.delete",
    access: "executive_set.access",
    create: "executive_set.create",
    detailsAccess: "executive_set.details.access",
    assign: "executive_set.assign",
  },
  logbook_diet: {
    access: "application_diet.access",
    create: "application_diet.create",
    approve: "application_diet.approve",
    reject: "application_diet.reject",
    activate: "application_diet.activate",
    start_assessment: "application_diet.start_assessment",
    close_assessment: "application_diet.close_assessment",
    extend: "application_diet.extend",
    delete: "application_diet.delete",
    detailsAccess: "application_diet.details.access",
    assign: "application_diet.assign",
    export: "application_diet.export",
  },
  logbook_result: {
    // Board approval of assessment-diet results before members can view them.
    approve: "logbook_result.approve",
  },
  application_diet: {
    access: "application_diet.access",
    create: "application_diet.create",
    approve: "application_diet.approve",
    reject: "application_diet.reject",
    activate: "application_diet.activate",
    start_assessment: "application_diet.start_assessment",
    close_assessment: "application_diet.close_assessment",
    extend: "application_diet.extend",
    delete: "application_diet.delete",
    detailsAccess: "application_diet.details.access",
    assign: "application_diet.assign",
    export: "application_diet.export",
    allCategoriesAccess: "application_diet.all.details.access",
    autoAssignToCommittee: "application_diet.committee.auto.assign",
    // Endorsing on behalf of chapters that never acted — its own privilege,
    // seeded to super_admin only, because it signs for someone else.
    chapterEndorseAll: "application_diet.chapter_endorse_all",
    // Issuing the entry number (S/P/T) to an approved applicant, which also
    // puts them on the members register. Its own privilege, seeded to
    // super_admin only: it is a different office from deciding applications,
    // and a number once issued cannot be taken back.
    memberNumberIssue: "probationer_applications.member_number.issue",
  },
  assessors: {
    access: "assessor.access",
    create: "assessor.create",
    delete: "assessor.delete",
    detailsAccess: "assessor.details.access",
    assign: "assessor.assign",
    export: "assessor.export",
  },
  member: {
    access: "member.access",
    detailsAccess: "member.details.access",
    update: "member.update",
    updateBasic: "member.update.basic",
    delete: "member.delete",
    viewFinancial: "member.financial.view",
    // Setting a temporary password on a member's account. Its own privilege,
    // not `update`: this hands over the ability to sign in as them.
    resetPassword: "member.password.reset",
    // Recording a death. Its own privilege, not `update`: it takes the member
    // out of the living register, and with it out of every mail audience, the
    // dues demand run, the analysis and any national office they hold.
    markDeceased: "member.deceased.mark",
    viewAudit: "member.audit.view",
  },
  support: {
    access: "support.access",
    adminAccess: "support.admin.access",
    assign: "support.assign",
    manage: "support.manage",
    categoryManage: "support.category.manage",
    viewAll: "support.view.all",
  },
  chapter: {
    access: "chapter.access",
    create: "chapter.create",
    update: "chapter.update",
    toogle: "chapter.togggle",
    delete: "chapter.delete",
    detailsAccess: "chapter.details.access",
    manageDiet: "chapter.diet.manage",
    dietAccess: "chapter.diet.access",
    assignLeadership: "chapter.leadership.assign",
    removeLeadership: "chapter.leadership.remove",
    dietCreate: "chapter.diet.create",
    dietView: "chapter.diet.access",
  },
  // Grade upgrades. `access` opens the admin section; `memberNumberIssue` is
  // held separately because issuing writes permanent numbers into the members
  // register from one Institute-wide series — reading the upgrade list and
  // stamping the register are not the same office. Seeded to super_admin only
  // (UpgradeMemberNumberPermissionSeeder); anyone else is granted it by name.
  upgrade: {
    access: "upgrade_applications.access",
    memberNumberIssue: "upgrade_applications.member_number.issue",
  },
  /**
   * Portal settings — fees, eligibility bars and the shape of the Institute's
   * running numbers. Its own permission rather than something the admin role
   * carries: holding an admin account is not the same office as setting the
   * Institute's fees. Seeded to super_admin only (SettingsPermissionSeeder).
   */
  settings: {
    manage: "settings.manage",
  },
  /**
   * Practice firms. Mirrors the backend's FirmPermissionSeeder exactly — these
   * are the same seven strings the API middleware checks.
   *
   * Split between the REGISTER (who is registered, and their money) and the
   * APPLICATIONS queue (paperwork under review), because the two are worked by
   * different offices: the accounting roles hold the register set and read
   * access to the queue, while deciding a registration belongs to the
   * secretariat and the Membership Committee.
   *
   * `issueNumber` is the narrowest of all: issuing a firm number puts a
   * practice on the Institute's register and binds its partners under the
   * one-firm-per-member rule.
   */
  firm: {
    // The register — /firm-registry
    access: "firms.access",
    export: "firms.export",
    account: "firms.account",
    payment: "firms.payment",

    // The applications queue — /admin/firm-applications
    applicationsAccess: "firm_applications.access",
    review: "firm_applications.review",
    issueNumber: "firm_applications.issue_number",
  },
};
